<template>
  <div class="container has-text-centered" v-if="camera">
    <h4 class="title is-4">{{camera.name}}</h4>
    <div class="container">
      <div v-if="topicValue" class="image_container">
        <img v-if="streamUrl" :src="streamUrl" @error="errorHandler" :style="imageStyle" class="image"/>
        <div v-else class="image_paused" :style="imageStyle">{{pausedMessage}}</div>
      </div>
      <div v-else>No snapshot path provided.</div>
    </div>
    <div class="container">
      <div v-if="topicForLastMotion" class="image_last_motion">
        <span>Last motion: {{lastMotionValue}}</span>
      </div>
      <div v-if="topicForSnapshotRating" class="image_rating">
        <span>Snapshot rating: {{snapshotRatingValue}} %</span>
      </div>
      <div v-if="topicForMotionRating" class="image_rating">
        <span>Motion rating: {{motionRatingValue}} %</span>
      </div>
      <div class="toggler">
        <b-switch v-model="isMotionCycle" @input="cycleMotionChanged">Cycle cameras at motion</b-switch>
      </div>
    </div>
  </div>
  <div v-else>
    <h4>Waiting for camera...</h4>
  </div>
</template>

<script>
  import {mapGetters, mapMutations} from 'vuex';
  import {formatTime, cameraStreamUrl} from "../utils/utils";
  import BSwitch from "buefy/src/components/switch/Switch";

  // How long to wait before re-opening a stream that dropped.
  const reconnectSeconds = 5;

  export default {
    components: { BSwitch },
    name: "screencameraview",
    props: ['id'],
    data() {
      return {
        camera: null,
        topicForSnapshot: null,
        topicForSnapshotInterval: null,
        topicForLastMotion: null,
        topicForSnapshotRating: null,
        topicForMotionRating: null,
        reconnectTimer: undefined,
        attempt: 0,
        isMotionCycle: false,
        generalLastMotionId: 0,
        generalLastMotionTimestamp: 0,
        isPageVisible: true
      };
    },
    watch: {
      serialized: {
        immediate: true,
        handler() {
          if (this.isMotionCycle) {
            const generalLastMotionId = this.mapping['live/cameras/lastMotionId'];
            const generalLastMotionTimestamp = this.mapping['live/cameras/lastMotionTimestamp'];
            if (generalLastMotionTimestamp > this.generalLastMotionTimestamp) {
              if (this.generalLastMotionId !== generalLastMotionId) {
                this.camera = this.cameraById(generalLastMotionId);
                this.refreshTopics();
              } else {
                this.refreshTopics();
              }
            }
          }
        }
      }
    },
    computed: {
      ...mapGetters('cameras', ['cameraById', 'motionCycle']),
      ...mapGetters('mqtt', ['valueForTopic', 'serialized', 'mapping']),
      ...mapGetters('ui', ['windowSizes']),
      topicValue() {
        return this.topicForSnapshot ? this.valueForTopic(this.topicForSnapshot) : null;
      },
      // Dropping the src is what actually closes the connection, which is what tells the
      // engine to stop capturing for this camera. There are no timers on this view: the
      // engine pushes every frame down the open stream.
      streamUrl() {
        // attempt < 0 means "disconnected, waiting to retry": returning null here is what
        // removes the <img> and closes the socket.
        if (!this.camera || !this.isPageVisible || this.attempt < 0) {
          return null;
        }

        return cameraStreamUrl(this.camera.id, this.attempt);
      },
      pausedMessage() {
        return this.isPageVisible ? 'Reconnecting...' : 'Paused';
      },
      lastMotionValue() {
        const timestampValue = this.topicForLastMotion ? this.valueForTopic(this.topicForLastMotion) : 0;

        return timestampValue > 0 ? formatTime(timestampValue) : '-';
      },
      snapshotRatingValue() {
        return this.topicForSnapshotRating ? this.valueForTopic(this.topicForSnapshotRating) : '-';
      },
      motionRatingValue() {
        return this.topicForMotionRating ? this.valueForTopic(this.topicForMotionRating) : '-';
      },
      imageWidth() {
        const w = this.windowSizes['width'];

        if (w > 1280) {
          return 1280;
        } else if (w > 1024) {
          return 1024;
        } else if (w > 768) {
          return 768;
        } else if (w >= 500) {
          return 480;
        } else if (w >= 425) {
          return 400;
        } else if (w >= 375) {
          return 340;
        }

        return 280;
      },
      imageHeight() {
        return this.windowSizes['height'];
      },
      imageStyle() {
        return `width: ${this.imageWidth}px;`;
      }
    },
    methods: {
      ...mapMutations('cameras', ['SET_MOTION_CYCLE']),
      clearTimers() {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = undefined;
      },
      // A dropped stream is normal (engine restart, network blip, camera down). Take the
      // <img> down so the browser really closes the socket, then open a fresh one.
      errorHandler() {
        this.clearTimers();

        const reconnectingFrom = this.attempt;
        this.attempt = -1;

        this.reconnectTimer = setTimeout(() => {
          this.attempt = reconnectingFrom + 1;
        }, reconnectSeconds * 1000);
      },
      visibilityHandler() {
        this.isPageVisible = !document.hidden;

        if (this.isPageVisible && this.attempt < 0) {
          this.attempt = 0;
        }
      },
      cycleMotionChanged() {
        this['SET_MOTION_CYCLE'](this.isMotionCycle);
      },
      refreshTopics() {
        if (this.camera && this.camera.topics) {
          const that = this;

          this.camera.topics.forEach(item => {
            if (item.name === "snapshotUrl") {
              that.topicForSnapshot = item.topic;
            } else if (item.name === "snapshotInterval") {
              that.topicForSnapshotInterval = item.topic;
            } else if (item.name === "lastMotion") {
              that.topicForLastMotion = item.topic;
            } else if (item.name === "ratingSnapshot") {
              that.topicForSnapshotRating = item.topic;
            } else if (item.name === "ratingMotion") {
              that.topicForMotionRating = item.topic;
            }
          });
        }
      }
    },
    mounted() {
      this.camera = this.cameraById(this.id);
      this.refreshTopics();
      this.isMotionCycle = this.motionCycle;
      this.generalLastMotionTimestamp = Math.floor(new Date().getTime() / 1000);
      this.isPageVisible = !document.hidden;

      document.addEventListener('visibilitychange', this.visibilityHandler);
    },
    destroyed() {
      this.clearTimers();

      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  }
</script>

<style scoped>
  .image_container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image {
    border: solid 1px #000000;
  }

  .image_paused {
    border: solid 1px #000000;
    padding: 3rem 0;
    opacity: 0.5;
  }

  .image_last_motion {
    margin-top: 1.25rem;
    margin-bottom: 1.25rem;
  }

  .image_rating {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
  }

  .toggler {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  }
</style>
